import clsx from 'clsx'
import { type ChangeEvent, lazy, Suspense, useMemo } from 'react'

import { FvButton, Icon } from '@fv/client-components'
import type { ListShipment } from '@fv/client-types'
import type { EquipmentType } from '@fv/models/equipment'

import { DividedContent } from '@/components/DividedContent'
import CheckboxField from '@/components/inputs/CheckboxField'
import { ListItem } from '@/components/List'
import { Loading } from '@/components/Loading'
import { ShipperTooltip } from '@/components/ShipperTooltip'
import { Price } from '@/components/shared/Price'
import { useTypeNames } from '@/hooks/settings'
import type { QuoteRequestResponse } from '@/hooks/shipments/useQuoteRequestResponses'
import { useRetractTender } from '@/hooks/shipments/useRetractTender'

import { HasPermission, usePermissions } from '../auth'
import { useWorkflowEnabled, WorkflowEnabled } from '../auth/HasWorkflow'
import { ChargeList } from '../charges/ChargeList'
import { ChargesSlider } from '../charges/ChargesSlider'
import { ChargesToggle } from '../charges/ChargesToggle'
import type { ChargeListQuote } from '../charges/types'
import { useChargeList } from '../charges/useChargeList'
import { useHasUnreadMessagesOnLoad } from '../messages/queries'
import { SubscribeButton } from '../messages/SubscribeButton'
import { CarrierSelectForm } from './CarrierSelectForm'
import { RetractTenderForm } from './RetractTenderForm'

type AltEquipmentTypeIconProps = {
  alternateTypes?: EquipmentType[]
  quotedEquipmentType?: EquipmentType
}

const AltEquipmentTypeIcon = ({
  alternateTypes = [],
  quotedEquipmentType,
}: AltEquipmentTypeIconProps) => {
  const { equipmentName } = useTypeNames()
  const altTypeIndex = quotedEquipmentType
    ? (alternateTypes?.indexOf(quotedEquipmentType) ?? -1)
    : -1

  if (!quotedEquipmentType || altTypeIndex < 0) return null

  return (
    <ShipperTooltip label={equipmentName(quotedEquipmentType)}>
      <div className="cursor-help border border-fv-blue bg-fv-beer-xlight text-[.75rem] leading-none p-[.15rem] rounded-xs">
        A{altTypeIndex + 1}
      </div>
    </ShipperTooltip>
  )
}

type MiniQuoteListItemProps = {
  awardedQuoteId?: string
  isActive: boolean
  isChecked: boolean
  load: ListShipment
  quote: QuoteRequestResponse
  readOnly?: boolean
  setActiveQuoteId: (id: string | undefined) => void
  setSelectedQuoteId: (id: string | undefined) => void
  canViewMessages?: boolean
}

const MiniMessageApp = lazy(() => import('../messages/MiniMessageApp'))

export const MiniQuoteListItem = ({
  awardedQuoteId,
  isActive,
  isChecked,
  load,
  quote,
  readOnly = false,
  canViewMessages = true,
  setActiveQuoteId,
  setSelectedQuoteId,
}: MiniQuoteListItemProps) => {
  const { canBook } = usePermissions()
  const workflowEnabled = useWorkflowEnabled(load.workflow)
  const { equipment, loadId, status: loadStatus } = load

  const { amount, carrierId, currency, id: quoteId, name } = quote
  const chargeQuote: ChargeListQuote = {
    _id: quote.id,
    amount: quote.amount,
    currency: quote.currency,
    providerName: quote.providerName,
    status: quote.status,
    source: quote.source,
    charges: quote.charges,
    isSpot: quote.isSpot,
    method: quote.method,
  }
  const chargeList = useChargeList(chargeQuote)

  const { canRetract } = useRetractTender()
  const hasUnreadMessages = useHasUnreadMessagesOnLoad({ carrierId, loadId })
  const isLoadCanceled = loadStatus === 'canceled'
  const isLoadAwarded = loadStatus === 'awarded'
  const isQuoteSelected = quoteId === awardedQuoteId
  const showRetender = isLoadAwarded && !isQuoteSelected
  const showRetract = useMemo(
    () => isChecked && canRetract(load) && isQuoteSelected && !readOnly,
    [isChecked, canRetract, load, isQuoteSelected, readOnly],
  )
  const showAward = useMemo(() => {
    if (
      !isChecked ||
      isLoadCanceled ||
      loadStatus === 'delivered' ||
      loadStatus === 'picked-up' ||
      showRetract
    )
      return false

    return loadStatus === 'pending' || !isQuoteSelected
  }, [isLoadCanceled, isChecked, loadStatus, showRetract, isQuoteSelected])

  const label = isQuoteSelected ? (
    <>
      <Icon icon="trophy" className="text-fv-orange" />
      <span>{name}</span>
    </>
  ) : (
    name
  )

  return (
    <ListItem
      className={clsx(
        'flex-wrap',

        isChecked &&
          ' bg-fv-blue-25 !py-4 !px-2 !border !border-fv-blue-200 relative z-20 first:mt-0 !border-r-0 !border-l-0 mb-4',
      )}
    >
      <div className="flex items-center w-full gap-x-2">
        {(readOnly || !canBook || !workflowEnabled) && (
          // add padding and negative margin so icon does not get cut off vertically by truncate
          <div className="flex-1 truncate py-2 -my-2">{label}</div>
          // end
        )}

        {canBook && !readOnly && workflowEnabled && (
          <CheckboxField
            className="checkbox flex-1"
            checked={isChecked}
            disabled={!amount}
            label={label}
            labelClassName="truncate"
            name={quoteId}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              const { checked } = e.target
              // Can't deselect awarded quote
              if (!checked && isQuoteSelected) return
              setSelectedQuoteId(checked ? quoteId : awardedQuoteId)
            }}
          />
        )}

        <AltEquipmentTypeIcon
          alternateTypes={equipment.alternateTypes}
          quotedEquipmentType={quote.equipmentType}
        />

        <div className="flex flex-nowrap items-center">
          {Boolean(amount) && (
            <HasPermission name="view-rates">
              <Price
                className="min-w-32 justify-end"
                amount={amount}
                currency={currency}
              />
            </HasPermission>
          )}
          <DividedContent className="ml-3 fa-width-auto">
            {!!amount && (
              <ChargesToggle
                chargeState={chargeList}
                onClick={() => {
                  chargeList.toggleViewing()
                  setActiveQuoteId(null)
                }}
              />
            )}

            {canViewMessages && (
              <>
                <ShipperTooltip label="View or send messages">
                  <div className="relative leading-none">
                    {hasUnreadMessages && (
                      <div className="shipment-list-pricing__unread-indicator animate-pulse" />
                    )}

                    <FvButton
                      icon={
                        hasUnreadMessages ? 'comment-alt-dots' : 'comment-alt'
                      }
                      onClick={() => {
                        setActiveQuoteId(isActive ? undefined : quoteId)
                        chargeList.setIsViewing(false)
                      }}
                    />
                  </div>
                </ShipperTooltip>
                <SubscribeButton loadId={loadId} carrierId={carrierId} />
              </>
            )}
          </DividedContent>
        </div>
      </div>

      {showRetract && (
        <WorkflowEnabled workflow={load.workflow}>
          <RetractTenderForm loadId={loadId} />
        </WorkflowEnabled>
      )}

      {showAward && (
        <WorkflowEnabled workflow={load.workflow}>
          <CarrierSelectForm
            onCancel={() => {
              setSelectedQuoteId(awardedQuoteId)
            }}
            retender={showRetender}
            load={load}
            quote={quote}
          />
        </WorkflowEnabled>
      )}

      {isActive && (
        <div className="my-4 w-full">
          <Suspense fallback={<Loading />}>
            <MiniMessageApp
              loadId={loadId}
              carrierId={carrierId}
              carrierName={name}
            />
          </Suspense>
        </div>
      )}
      {chargeList.isViewing && (
        <ChargeList
          isChecked={isChecked}
          chargeState={chargeList}
          load={load}
          quote={chargeQuote}
        />
      )}

      <ChargesSlider chargeState={chargeList} load={load} />
    </ListItem>
  )
}
